import React from 'react';
import { Clock, Shield, Check, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';

export function ScheduleStep({ fee, currencySymbol, calendarComponent, dateError, selectedDate, slots, slotsLoading, selectedTime, setSelectedTime }) {
  return (
    <div className="space-y-4" data-testid="onboarding-step-schedule">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-emerald-800">Session Fee</span>
          {fee ? (
            <span className="text-lg font-bold text-emerald-700">{currencySymbol}{fee.fee_amount}</span>
          ) : (
            <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
          )}
        </div>
        <p className="text-xs text-emerald-600 mt-0.5">60-minute guided session</p>
      </div>
      {calendarComponent}
      {dateError && <p className="text-xs text-red-500 mt-1">{dateError}</p>}
      {selectedDate && slots.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-600 mb-2">
            <Clock className="w-3 h-3 inline mr-1" />
            Available time slots for {selectedDate}
          </p>
          <div className="grid grid-cols-4 gap-2">
            {slots.map(s => (
              <button key={s.time} onClick={() => setSelectedTime(s.time)}
                className={`text-xs py-2 px-1 rounded-md border transition-colors ${selectedTime === s.time ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-200 hover:border-emerald-400 text-slate-600'}`}
                data-testid={`slot-${s.time}`}>{s.label}</button>
            ))}
          </div>
        </div>
      )}
      {selectedDate && !slotsLoading && slots.length === 0 && !dateError && (
        <p className="text-xs text-amber-600">No available slots for this date</p>
      )}
    </div>
  );
}

export function PrivacyStep({ maskedMode, setMaskedMode, notes, setNotes }) {
  return (
    <div className="space-y-4" data-testid="onboarding-step-privacy">
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" />
            <span className="font-medium text-sm">Enable Masked Mode for Privacy</span>
          </div>
          <Switch checked={maskedMode} onCheckedChange={setMaskedMode} data-testid="masked-mode-toggle" />
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">Your data will be hidden from the support assistant. Only structure and field inputs will be visible.</p>
        <div className="mt-3 space-y-1.5">
          {['Monetary values are replaced with placeholders', 'Asset names are partially masked', 'Sensitive identifiers are concealed', 'Support sees field names, form structure & completeness only'].map(text => (
            <div key={text} className="flex items-center gap-2 text-xs text-slate-500">
              <Check className="w-3 h-3 text-emerald-500" /><span>{text}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Notes for support (optional)</label>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="e.g., Need help uploading CSV files, understanding asset allocation..." rows={3} className="text-sm" data-testid="onboarding-notes" />
      </div>
    </div>
  );
}

export function ReviewStep({ selectedDate, selectedTime, maskedMode, notes, currencySymbol, fee }) {
  return (
    <div className="space-y-3" data-testid="onboarding-step-review">
      <h3 className="font-medium text-sm text-slate-700">Review your request</h3>
      <div className="bg-slate-50 rounded-lg p-4 space-y-3">
        <div className="flex justify-between text-sm"><span className="text-slate-500">Date</span><span className="font-medium">{selectedDate}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">Time</span><span className="font-medium">{selectedTime} (60 min)</span></div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Privacy Mode</span>
          <Badge variant={maskedMode ? "default" : "secondary"} className={maskedMode ? "bg-emerald-100 text-emerald-700" : ""}>{maskedMode ? 'Masked' : 'Full View'}</Badge>
        </div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">Fee</span><span className="font-bold text-emerald-700">{currencySymbol}{fee?.fee_amount}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">Payment</span><span className="capitalize">{fee?.payment_method}</span></div>
        {notes && (
          <div className="text-sm border-t pt-2 mt-2">
            <span className="text-slate-500 block mb-1">Your notes:</span>
            <p className="text-slate-700 text-xs">{notes}</p>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-400 text-center">You'll be prompted to pay after your request is confirmed by support.</p>
    </div>
  );
}
